/* Pure Shenandoah store locator -- section-scoped and Theme Editor safe. */

if (!customElements.get('store-locator')) {
  customElements.define(
    'store-locator',
    class extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.listeners = [];
        this.selectedProducts = new Set();
        this.defaultRadius = Number(this.dataset.defaultRadius) || 25;
        this.radius = this.defaultRadius;
        this.origin = null;
        this.activeLocationId = null;
        this.searchController = null;
        this.notice = this.querySelector('[data-locator-notice]');
        this.waitForDependencies();
      }

      disconnectedCallback() {
        this.searchController?.abort();
        window.clearTimeout(this.dependencyTimer);
        this.listeners.forEach(({ target, event, handler, options }) =>
          target.removeEventListener(event, handler, options),
        );
        this.listeners = [];
        if (this.map) {
          this.map.remove();
          this.map = null;
        }
        this.initialized = false;
      }

      waitForDependencies(attempt = 0) {
        if (window.L && window.PureStoreLocatorData) {
          this.initialize();
          return;
        }

        if (attempt >= 100) {
          this.setNotice('The store finder could not load. Please refresh the page and try again.', 'error');
          return;
        }

        this.dependencyTimer = window.setTimeout(() => this.waitForDependencies(attempt + 1), 50);
      }

      initialize() {
        this.searchForm = this.querySelector('[data-locator-search]');
        this.queryInput = this.querySelector('[data-locator-query]');
        this.notice = this.querySelector('[data-locator-notice]');
        this.workspace = this.querySelector('[data-locator-workspace]');
        this.resultsPanel = this.querySelector('.pure-locator__results-panel');
        this.resultsElement = this.querySelector('[data-locator-results]');
        this.summaryElement = this.querySelector('[data-result-summary]');
        this.clearSearchButton = this.querySelector('[data-clear-search]');
        this.radiusMenu = this.querySelector('[data-radius-menu]');
        this.radiusLabel = this.querySelector('[data-radius-label]');
        this.productMenu = this.querySelector('[data-product-menu]');
        this.productLabel = this.querySelector('[data-product-label]');
        this.productOptions = this.querySelector('[data-product-options]');
        this.useLocationButton = this.querySelector('[data-use-location]');
        this.selectedPanel = this.querySelector('[data-selected-store]');
        this.selectedName = this.querySelector('[data-selected-name]');
        this.selectedProductsElement = this.querySelector('[data-selected-products]');
        this.selectedAddress = this.querySelector('[data-selected-address]');
        this.selectedContact = this.querySelector('[data-selected-contact]');
        this.selectedActions = this.querySelector('[data-selected-actions]');
        this.locations = window.PureStoreLocatorData.locations.map((location) => ({
          ...location,
          tags: [...new Set(location.tags)],
        }));
        this.products = window.PureStoreLocatorData.products;
        this.productMap = new Map(this.products.map((product) => [product.tag, product]));
        this.workspace.hidden = false;
        this.renderProductOptions();
        this.initializeMap();
        this.bindEvents();
        this.setRadius(this.defaultRadius, false);
        this.applyFilters({ fitMap: true });
      }

      addListener(target, event, handler, options) {
        target.addEventListener(event, handler, options);
        this.listeners.push({ target, event, handler, options });
      }

      bindEvents() {
        this.addListener(this.searchForm, 'submit', (event) => this.search(event));
        this.addListener(this.useLocationButton, 'click', () => this.useCurrentLocation());
        this.addListener(this.clearSearchButton, 'click', () => this.clearSearch());
        this.addListener(this.queryInput, 'search', () => {
          if (!this.queryInput.value) this.clearSearch();
        });

        this.querySelectorAll('[data-radius]').forEach((button) => {
          this.addListener(button, 'click', () => this.setRadius(Number(button.dataset.radius), true));
        });

        this.addListener(this.productOptions, 'change', (event) => {
          const input = event.target.closest('input[data-product-tag]');
          if (!input) return;
          if (input.checked) this.selectedProducts.add(input.dataset.productTag);
          else this.selectedProducts.delete(input.dataset.productTag);
          this.updateProductLabel();
          this.applyFilters({ fitMap: true });
        });

        this.addListener(this.querySelector('[data-clear-products]'), 'click', () => this.clearProducts());
        this.addListener(this.querySelector('[data-close-products]'), 'click', () => {
          this.productMenu.open = false;
        });
        this.addListener(this.querySelector('[data-close-selected]'), 'click', () => this.closeSelected());

        [this.radiusMenu, this.productMenu].forEach((menu) => {
          this.addListener(menu, 'toggle', () => {
            if (!menu.open) return;
            [this.radiusMenu, this.productMenu].forEach((other) => {
              if (other !== menu) other.open = false;
            });
          });
        });

        this.addListener(document, 'click', (event) => {
          if (!this.contains(event.target)) {
            this.radiusMenu.open = false;
            this.productMenu.open = false;
          }
        });

        this.addListener(this, 'keydown', (event) => {
          if (event.key !== 'Escape') return;
          this.radiusMenu.open = false;
          this.productMenu.open = false;
          if (!this.selectedPanel.hidden) this.closeSelected();
        });
      }

      initializeMap() {
        this.map = window.L.map(this.querySelector('[data-locator-map]'), {
          center: [37.75, -78.6],
          zoom: 6,
          minZoom: 4,
          maxZoom: 18,
          scrollWheelZoom: false,
          zoomControl: false,
        });

        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(this.map);

        window.L.control.zoom({ position: 'topright' }).addTo(this.map);
        this.markerLayer = window.L.layerGroup().addTo(this.map);
        this.markers = new Map();

        this.locations.forEach((location) => {
          const marker = window.L.marker([location.lat, location.lng], {
            icon: this.markerIcon(false),
            title: location.name,
            keyboard: true,
          });
          marker.on('click', () => this.selectLocation(location.id, { scrollResult: true }));
          this.markers.set(location.id, marker);
        });
      }

      markerIcon(active) {
        return window.L.divIcon({
          className: 'pure-locator__marker-wrap',
          html: `<span class="pure-locator__marker${active ? ' pure-locator__marker--active' : ''}" aria-hidden="true"><svg viewBox="0 0 34 44"><path d="M17 1.5C8.6 1.5 2 8.1 2 16.5 2 27.5 17 42.5 17 42.5s15-15 15-26C32 8.1 25.4 1.5 17 1.5Z" fill="#0f9291" stroke="#004e4a" stroke-width="2"/><circle cx="17" cy="16.5" r="5.5" fill="#f7f6f0" stroke="#004e4a" stroke-width="2"/></svg></span>`,
          iconSize: [34, 44],
          iconAnchor: [17, 43],
          popupAnchor: [0, -39],
        });
      }

      renderProductOptions() {
        const fragment = document.createDocumentFragment();
        this.products.forEach((product) => {
          const label = document.createElement('label');
          label.className = 'pure-locator__product-option';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.value = product.tag;
          input.dataset.productTag = product.tag;
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = '';
          image.width = 44;
          image.height = 44;
          image.loading = 'lazy';
          image.addEventListener('error', () => image.remove(), { once: true });
          const text = document.createElement('span');
          text.textContent = product.label;
          label.append(input, image, text);
          fragment.append(label);
        });
        this.productOptions.replaceChildren(fragment);
      }

      renderResults(locations) {
        const fragment = document.createDocumentFragment();

        if (!locations.length) {
          const empty = document.createElement('div');
          empty.className = 'pure-locator__empty';
          const heading = document.createElement('h3');
          heading.textContent = 'No retailers found';
          const message = document.createElement('p');
          message.textContent = this.origin
            ? 'Try a larger radius or remove a product filter.'
            : 'Remove a product filter to see more retailers.';
          empty.append(heading, message);
          fragment.append(empty);
        }

        locations.forEach((location) => {
          const result = document.createElement('button');
          result.type = 'button';
          result.className = 'pure-locator__result';
          result.dataset.locationId = location.id;
          result.setAttribute('aria-current', String(location.id === this.activeLocationId));

          const topline = document.createElement('span');
          topline.className = 'pure-locator__result-topline';
          const name = document.createElement('span');
          name.className = 'pure-locator__result-name';
          this.renderLocationName(name, location);
          topline.append(name);

          if (Number.isFinite(location.distance)) {
            const distance = document.createElement('span');
            distance.className = 'pure-locator__distance';
            distance.textContent = `${this.formatDistance(location.distance)} mi`;
            topline.append(distance);
          }

          result.append(topline);
          if (location.tags.length) result.append(this.buildBadges(location.tags));
          const address = document.createElement('span');
          address.className = 'pure-locator__address';
          address.textContent = location.address;
          result.append(address);
          result.addEventListener('click', () => this.selectLocation(location.id));
          fragment.append(result);
        });

        this.resultsElement.replaceChildren(fragment);
      }

      renderLocationName(container, location) {
        container.replaceChildren();
        container.classList.add('pure-locator__branded-name');
        if (/^total\s+wine\b/i.test(location.name) && this.dataset.totalWineLogo) {
          // Frame only the grape emblem in the existing 1000px partner asset.
          // Keep the full wordmark unchanged for its other uses in the theme.
          const svgNamespace = 'http://www.w3.org/2000/svg';
          const logo = document.createElementNS(svgNamespace, 'svg');
          logo.setAttribute('viewBox', '655 240 245 285');
          logo.setAttribute('width', '34');
          logo.setAttribute('height', '40');
          logo.setAttribute('aria-hidden', 'true');
          logo.setAttribute('focusable', 'false');
          logo.setAttribute('class', 'pure-locator__retailer-logo');
          const artwork = document.createElementNS(svgNamespace, 'image');
          artwork.setAttribute('href', this.dataset.totalWineLogo);
          artwork.setAttribute('width', '1000');
          artwork.setAttribute('height', '1000');
          artwork.addEventListener('error', () => logo.remove(), { once: true });
          logo.append(artwork);
          container.append(logo);
        }
        const label = document.createElement('span');
        label.textContent = location.name;
        container.append(label);
      }

      buildBadges(tags) {
        const badges = document.createElement('span');
        badges.className = 'pure-locator__badges';
        tags.forEach((tag) => {
          const product = this.productMap.get(tag);
          if (!product) return;
          const badge = document.createElement('span');
          badge.className = 'pure-locator__badge';
          badge.style.setProperty('--product-color', product.color);
          badge.style.setProperty('--product-ink', this.contrastColor(product.color));
          if (product.image) {
            const image = document.createElement('img');
            image.src = product.image;
            image.alt = '';
            image.width = 36;
            image.height = 36;
            image.loading = 'lazy';
            image.addEventListener('error', () => image.remove(), { once: true });
            badge.append(image);
          }
          const label = document.createElement('span');
          label.textContent = product.label;
          badge.append(label);
          badges.append(badge);
        });
        return badges;
      }

      applyFilters({ fitMap = false } = {}) {
        const filtered = this.locations
          .map((location) => ({
            ...location,
            distance: this.origin
              ? this.distanceInMiles(this.origin.lat, this.origin.lng, location.lat, location.lng)
              : null,
          }))
          .filter((location) => {
            const withinRadius = !this.origin || location.distance <= this.radius;
            const carriesProducts = [...this.selectedProducts].every((tag) => location.tags.includes(tag));
            return withinRadius && carriesProducts;
          })
          .sort((left, right) => {
            if (this.origin) return left.distance - right.distance;
            return left.name.localeCompare(right.name);
          });

        this.filteredLocations = filtered;
        if (this.activeLocationId && !filtered.some((location) => location.id === this.activeLocationId)) {
          this.closeSelected({ returnFocus: false });
        }
        this.renderResults(filtered);
        this.updateMarkers(filtered);
        this.updateSummary(filtered.length);
        if (fitMap) this.fitMap(filtered);
      }

      updateMarkers(locations) {
        this.markerLayer.clearLayers();
        locations.forEach((location) => this.markerLayer.addLayer(this.markers.get(location.id)));
      }

      updateSummary(count) {
        const retailerWord = count === 1 ? 'retailer' : 'retailers';
        if (this.origin) {
          this.summaryElement.textContent = `${count} ${retailerWord} within ${this.radius} miles`;
        } else if (this.selectedProducts.size) {
          this.summaryElement.textContent = `${count} matching ${retailerWord}`;
        } else {
          this.summaryElement.textContent = `${count} retail locations`;
        }
        this.clearSearchButton.hidden = !this.origin;
      }

      fitMap(locations) {
        if (!this.map) return;
        const points = locations.map((location) => [location.lat, location.lng]);
        if (this.origin) points.push([this.origin.lat, this.origin.lng]);
        if (!points.length) {
          if (this.origin) this.map.setView([this.origin.lat, this.origin.lng], 9);
          return;
        }
        if (points.length === 1) {
          this.map.setView(points[0], 12);
          return;
        }
        this.map.fitBounds(window.L.latLngBounds(points), { padding: [44, 44], maxZoom: this.origin ? 11 : 8 });
      }

      selectLocation(id, { scrollResult = false } = {}) {
        const location = this.locations.find((item) => item.id === id);
        const marker = this.markers.get(id);
        if (!location || !marker) return;

        if (this.activeLocationId && this.markers.has(this.activeLocationId)) {
          const previousMarker = this.markers.get(this.activeLocationId);
          previousMarker.setIcon(this.markerIcon(false));
          previousMarker.setZIndexOffset(0);
        }
        this.activeLocationId = id;
        marker.setIcon(this.markerIcon(true));
        marker.setZIndexOffset(1000);
        this.resultsElement.querySelectorAll('[data-location-id]').forEach((result) => {
          result.setAttribute('aria-current', String(result.dataset.locationId === id));
        });

        if (scrollResult) {
          this.resultsElement
            .querySelector(`[data-location-id="${CSS.escape(id)}"]`)
            ?.scrollIntoView({ block: 'nearest', behavior: this.reducedMotion() ? 'auto' : 'smooth' });
        }

        this.renderSelectedLocation(location);
        this.map.setView([location.lat, location.lng], Math.max(this.map.getZoom(), 13), {
          animate: !this.reducedMotion(),
        });
      }

      renderSelectedLocation(location) {
        this.renderLocationName(this.selectedName, location);
        this.selectedAddress.textContent = location.address;

        const products = this.buildBadges(location.tags);
        products.classList.add('pure-locator__badges--selected');
        if (!products.childElementCount) {
          const unavailable = document.createElement('p');
          unavailable.className = 'pure-locator__products-unlisted';
          unavailable.textContent = 'Product availability is not listed for this retailer.';
          this.selectedProductsElement.replaceChildren(unavailable);
        } else {
          const label = document.createElement('p');
          label.className = 'pure-locator__selected-label';
          label.textContent = 'Available products';
          this.selectedProductsElement.replaceChildren(label, products);
        }

        const contacts = document.createDocumentFragment();
        const websiteUrl = this.websiteUrl(location.website);
        if (websiteUrl) {
          const website = document.createElement('a');
          website.href = websiteUrl;
          website.target = '_blank';
          website.rel = 'noopener';
          website.textContent = websiteUrl.replace(/^https?:\/\//i, '').replace(/\/$/, '');
          contacts.append(website);
        }
        if (location.phone) {
          const phone = document.createElement('a');
          phone.href = `tel:${location.phone.replace(/[^+\d]/g, '')}`;
          phone.textContent = location.phone;
          contacts.append(phone);
        }
        this.selectedContact.replaceChildren(contacts);

        const directions = document.createElement('a');
        directions.href = this.directionsUrl(location);
        directions.target = '_blank';
        directions.rel = 'noopener';
        directions.textContent = 'Directions';
        this.selectedActions.replaceChildren(directions);
        this.selectedPanel.hidden = false;
        this.resultsPanel.classList.add('is-showing-details');
        this.selectedPanel.scrollTop = 0;
        this.selectedPanel.focus({ preventScroll: true });
      }

      closeSelected({ returnFocus = true } = {}) {
        if (this.selectedPanel.hidden) return;
        const previousId = this.activeLocationId;
        if (previousId && this.markers.has(previousId)) {
          const previousMarker = this.markers.get(previousId);
          previousMarker.setIcon(this.markerIcon(false));
          previousMarker.setZIndexOffset(0);
        }
        this.activeLocationId = null;
        this.selectedPanel.hidden = true;
        this.resultsPanel.classList.remove('is-showing-details');
        this.resultsElement.querySelectorAll('[data-location-id]').forEach((result) => {
          result.setAttribute('aria-current', 'false');
        });
        if (returnFocus && previousId) {
          this.resultsElement.querySelector(`[data-location-id="${CSS.escape(previousId)}"]`)?.focus({
            preventScroll: true,
          });
        }
      }

      async search(event) {
        event.preventDefault();
        const query = this.queryInput.value.trim();
        if (!query) {
          this.queryInput.focus();
          return;
        }

        this.searchController?.abort();
        const controller = new AbortController();
        this.searchController = controller;
        this.searchForm.setAttribute('aria-busy', 'true');
        this.setNotice('Searching OpenStreetMap…');

        try {
          const cached = this.readCachedSearch(query);
          const result = cached || (await this.geocode(query, controller.signal));
          if (controller.signal.aborted || !this.isConnected) return;
          if (!result) {
            this.setNotice('We could not find that place. Try a city and state or a five-digit ZIP code.', 'error');
            return;
          }
          this.cacheSearch(query, result);
          this.setOrigin(result.lat, result.lng);
          this.setNotice(`Showing retailers near ${result.label}.`);
        } catch (error) {
          if (error.name !== 'AbortError') {
            this.setNotice(
              'Location search is temporarily unavailable. You can still browse every retailer on the map.',
              'error',
            );
          }
        } finally {
          if (this.searchController === controller) this.searchForm.removeAttribute('aria-busy');
        }
      }

      async geocode(query, signal) {
        const url = new URL('https://nominatim.openstreetmap.org/search');
        url.search = new URLSearchParams({
          q: query,
          format: 'jsonv2',
          limit: '1',
          countrycodes: 'us',
          'accept-language': document.documentElement.lang || 'en',
        });
        const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Geocoding failed (${response.status})`);
        const [result] = await response.json();
        if (!result) return null;
        return { lat: Number(result.lat), lng: Number(result.lon), label: result.display_name };
      }

      useCurrentLocation() {
        if (!navigator.geolocation) {
          this.setNotice(
            'Your browser does not support location sharing. Search by city or ZIP code instead.',
            'error',
          );
          return;
        }

        this.useLocationButton.disabled = true;
        this.setNotice('Finding your location…');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.useLocationButton.disabled = false;
            this.queryInput.value = 'My location';
            this.setOrigin(position.coords.latitude, position.coords.longitude);
            this.setNotice('Showing retailers near your current location.');
          },
          () => {
            this.useLocationButton.disabled = false;
            this.setNotice(
              'We could not access your location. Check your browser permission or search by ZIP code.',
              'error',
            );
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
        );
      }

      setOrigin(lat, lng) {
        this.origin = { lat, lng };
        if (this.originMarker) this.map.removeLayer(this.originMarker);
        this.originMarker = window.L.marker([lat, lng], {
          icon: window.L.divIcon({
            className: 'pure-locator__origin-wrap',
            html: '<span class="pure-locator__origin"></span>',
            iconSize: [22, 22],
            iconAnchor: [11, 11],
          }),
          interactive: false,
        }).addTo(this.map);
        this.applyFilters({ fitMap: true });
      }

      clearSearch() {
        this.searchController?.abort();
        this.origin = null;
        this.queryInput.value = '';
        if (this.originMarker) {
          this.map.removeLayer(this.originMarker);
          this.originMarker = null;
        }
        this.setNotice('');
        this.applyFilters({ fitMap: true });
      }

      setRadius(radius, apply) {
        this.radius = radius;
        this.radiusLabel.textContent = `${radius} mi`;
        this.querySelectorAll('[data-radius]').forEach((button) => {
          button.setAttribute('aria-checked', String(Number(button.dataset.radius) === radius));
        });
        this.radiusMenu.open = false;
        if (apply) this.applyFilters({ fitMap: true });
      }

      clearProducts() {
        this.selectedProducts.clear();
        this.productOptions.querySelectorAll('input').forEach((input) => {
          input.checked = false;
        });
        this.updateProductLabel();
        this.applyFilters({ fitMap: true });
      }

      updateProductLabel() {
        const count = this.selectedProducts.size;
        this.productLabel.textContent = count ? `Products (${count})` : 'Filter by product';
      }

      setNotice(message, tone = 'neutral') {
        if (!this.notice) return;
        this.notice.textContent = message;
        this.notice.dataset.tone = tone;
      }

      directionsUrl(location) {
        const destination = encodeURIComponent(`${location.lat},${location.lng}`);
        return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
      }

      websiteUrl(value) {
        if (!value) return null;
        const candidate = /^https?:\/\//i.test(value) ? value : `https://${value}`;
        try {
          const url = new URL(candidate);
          return url.hostname.includes('.') ? url.href : null;
        } catch (_error) {
          return null;
        }
      }

      distanceInMiles(lat1, lng1, lat2, lng2) {
        const toRadians = (degrees) => (degrees * Math.PI) / 180;
        const earthRadiusMiles = 3958.8;
        const deltaLat = toRadians(lat2 - lat1);
        const deltaLng = toRadians(lng2 - lng1);
        const a =
          Math.sin(deltaLat / 2) ** 2 +
          Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(deltaLng / 2) ** 2;
        return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      }

      formatDistance(distance) {
        if (distance < 10) return distance.toFixed(1);
        return Math.round(distance).toString();
      }

      contrastColor(hex) {
        const value = String(hex || '').replace('#', '');
        if (!/^[0-9a-f]{6}$/i.test(value)) return '#fff';
        const [red, green, blue] = value.match(/.{2}/g).map((channel) => parseInt(channel, 16));
        const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
        return luminance > 0.66 ? '#171918' : '#fff';
      }

      readCachedSearch(query) {
        try {
          const cached = JSON.parse(sessionStorage.getItem(`pure-location:${query.toLocaleLowerCase('en-US')}`));
          return cached && Number.isFinite(cached.lat) && Number.isFinite(cached.lng) ? cached : null;
        } catch (_error) {
          return null;
        }
      }

      cacheSearch(query, result) {
        try {
          sessionStorage.setItem(`pure-location:${query.toLocaleLowerCase('en-US')}`, JSON.stringify(result));
        } catch (_error) {
          // Storage can be unavailable in privacy modes; search still works.
        }
      }

      reducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    },
  );
}
